import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getNotificationById,
  createNotification,
  updateNotification,
  deleteNotification,
  getUserNotifications,
  getUnreadCount,
  markAllAsRead,
  markNotificationAsRead,
} from "./notification.api";
import type {
  GetNotificationsQuery,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  GetUserNotificationsQuery,
} from "./notification.type";

// Query keys
export const notificationKeys = {
  all: ["notifications"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (query: GetNotificationsQuery) =>
    [...notificationKeys.lists(), query] as const,
  details: () => [...notificationKeys.all, "detail"] as const,
  detail: (id: string) => [...notificationKeys.details(), id] as const,
  // User notification keys
  user: () => [...notificationKeys.all, "user"] as const,
  userList: (query: GetUserNotificationsQuery) =>
    [...notificationKeys.user(), "list", query] as const,
  unreadCount: () => [...notificationKeys.user(), "unread-count"] as const,
};

// Get notifications list query
export const useNotifications = (query: GetNotificationsQuery = {}) => {
  return useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => getNotifications(query),
    placeholderData: keepPreviousData,
  });
};

// Get notification by ID query
export const useNotification = (id: string) => {
  return useQuery({
    queryKey: notificationKeys.detail(id),
    queryFn: () => getNotificationById(id),
    enabled: !!id, // Only run if id is truthy
  });
};

// Create notification mutation
export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNotificationRequest) =>
      createNotification(data),
    onSuccess: () => {
      // Invalidate notifications list queries
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
    },
    onError: (error) => {
      console.error("Create notification failed:", error);
    },
  });
};

// Update notification mutation
export const useUpdateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateNotificationRequest;
    }) => updateNotification({ id, data }),
    onSuccess: (_, variables) => {
      // Invalidate specific notification detail
      queryClient.invalidateQueries({
        queryKey: notificationKeys.detail(variables.id),
      });
      // Invalidate notifications list queries
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
    },
    onError: (error) => {
      console.error("Update notification failed:", error);
    },
  });
};

// Delete notification mutation
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => {
      // Invalidate notifications list queries
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });
    },
    onError: (error) => {
      console.error("Delete notification failed:", error);
    },
  });
};

// User notification hooks

// Get user notifications list query
export const useUserNotifications = (
  query: GetUserNotificationsQuery = {}
) => {
  return useQuery({
    queryKey: notificationKeys.userList(query),
    queryFn: () => getUserNotifications(query),
  });
};

// Get unread count query
export const useUnreadCount = () => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => getUnreadCount(),
    // Removed refetchInterval: WebSocket handles real-time updates
  });
};

// Mark all notifications as read mutation
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllAsRead(),
    onSuccess: () => {
      // Invalidate user notifications and unread count
      queryClient.invalidateQueries({
        queryKey: notificationKeys.user(),
      });
    },
    onError: (error) => {
      console.error("Mark all as read failed:", error);
    },
  });
};

// Mark single notification as read mutation
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationAsRead(id),
    onSuccess: () => {
      // Invalidate user notifications and unread count
      queryClient.invalidateQueries({
        queryKey: notificationKeys.user(),
      });
    },
    onError: (error) => {
      console.error("Mark notification as read failed:", error);
    },
  });
};

